import type { ComponentProps, FC, ReactElement, ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { MatchMedia, StripeHeader, StripeProductInfo } from '@george-labs.com/design-system';

import { LocalizedAmount } from '@shared/core-ui-amount';
import { useGlobalColor } from '@shared/core-ui-layout';
import { t } from '@luna/i18n';
import type { Amount } from '@shared/core-types';

type Props = Omit<ComponentProps<typeof StripeHeader>, 'left'> & {
    amount?: Amount;
    alias?: string;
    productType?: string;
    avatar: ReactElement;
    leftButton?: ReactNode;
};

export const ProductsStripeHeader: FC<Props> = ({
    amount,
    alias,
    productType,
    avatar,
    leftButton,
    ...passThroughStripeProps
}) => {
    const { value: color } = useGlobalColor();
    const centerRef = useRef<HTMLDivElement | null>(null);

    const { center, ...otherStripeProps } = passThroughStripeProps;

    const centerWithFocus = (
        <div ref={centerRef} tabIndex={-1}>
            {center}
        </div>
    );

    const amounts = amount && (
        <LocalizedAmount data-cy="stripe-product-info-amount" showCurrency amount={amount} />
    );

    useLayoutEffect(() => {
        centerRef.current?.focus();
    }, []);

    const productsInfo = (
        <StripeProductInfo
            amount={amounts}
            avatar={
                <avatar.type
                    {...(avatar.props as Record<string, never>)}
                    data-cy="stripe-product-info-icon"
                />
            }
            amountLabel={t('main.menu.v2.products.detail.amountLabel_a11y')}
            alias={<span data-cy="stripe-product-info-alias">{alias}</span>}
            aliasLabel={t('main.menu.v2.products.detail.aliasLabel_a11y')}
            type={productType}
            typeLabel={t('main.menu.v2.products.detail.typeLabel_a11y')}
            data-cy="stripe-product-info"
            backButton={leftButton}
        />
    );

    return (
        <MatchMedia max={MatchMedia.TIER.MD}>
            {({ isMatching }) => {
                const left = isMatching ? productsInfo : leftButton;

                return (
                    <StripeHeader
                        data-cy="stripe-header"
                        left={left}
                        color={color}
                        center={centerWithFocus}
                        {...otherStripeProps}
                    />
                );
            }}
        </MatchMedia>
    );
};
